import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Building2, Mail, Phone, Archive, RotateCcw, Eye, EyeOff, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { motion } from "framer-motion";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const Customers = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showOnlyWithBalance, setShowOnlyWithBalance] = useState(searchParams.get('filter') === 'receivable');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    notes: ""
  });

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers?include_inactive=${showInactive}`, { withCredentials: true });
      setCustomers(response.data);
    } catch (error) {
      toast.error("Müşteriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleArchive = async (id) => {
    if (!window.confirm("Bu müşteriyi pasife almak istediğinizden emin misiniz?")) return;
    try {
      await axios.post(`${API}/customers/${id}/archive`, {}, { withCredentials: true });
      toast.success("Müşteri arşivlendi");
      fetchCustomers();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.post(`${API}/customers/${id}/restore`, {}, { withCredentials: true });
      toast.success("Müşteri aktife alındı");
      fetchCustomers();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, formData, { withCredentials: true });
        toast.success("Müşteri güncellendi");
      } else {
        await axios.post(`${API}/customers`, formData, { withCredentials: true });
        toast.success("Müşteri eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu müşteriyi silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/customers/${id}`, { withCredentials: true });
      toast.success("Müşteri silindi");
      fetchCustomers();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      company: customer.company || "",
      address: customer.address || "",
      notes: customer.notes || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({ name: "", email: "", phone: "", company: "", address: "", notes: "" });
  };

  // Filtreleme: arama + alacağı olanlar
  const filteredCustomers = customers.filter(c => {
    // Arama filtresi
    const matchesSearch = c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.company?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase());
    
    // Alacağı olanlar filtresi
    const matchesBalance = showOnlyWithBalance ? (c.total_receivable > 0) : true;
    
    return matchesSearch && matchesBalance;
  });

  // Müşteri detayına git
  const handleRowClick = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Müşteriler</h1>
          <p className="text-zinc-500 text-sm mt-1">
            {customers.filter(c => c.is_active !== false).length} aktif, {customers.filter(c => c.is_active === false).length} pasif müşteri
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
          data-testid="add-customer-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Müşteri
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input 
            placeholder="Müşteri ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800"
            data-testid="customer-search"
          />
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-sm">
          <Switch 
            checked={showOnlyWithBalance}
            onCheckedChange={setShowOnlyWithBalance}
            data-testid="show-with-balance"
          />
          <Label className="text-zinc-400 text-sm">
            Sadece alacağı olanlar
          </Label>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-sm">
          <Switch 
            checked={showInactive}
            onCheckedChange={setShowInactive}
            data-testid="show-inactive-customers"
          />
          <Label className="text-zinc-400 text-sm flex items-center gap-2">
            {showInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            Pasif müşteriler
          </Label>
        </div>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Müşteri</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">İletişim</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Alacak</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Borç</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer, index) => {
                const isActive = customer.is_active !== false;
                return (
                <motion.tr 
                  key={customer.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleRowClick(customer.id)}
                  className={`border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors cursor-pointer ${!isActive ? 'opacity-50' : ''}`}
                  data-testid={`customer-row-${customer.id}`}
                >
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${isActive ? 'bg-zinc-800' : 'bg-rose-900/30'}`}>
                        <Building2 className={`w-5 h-5 ${isActive ? 'text-zinc-500' : 'text-rose-400'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-50 flex items-center gap-2">
                          {customer.name}
                          {!isActive && <span className="text-xs px-1.5 py-0.5 bg-rose-900/30 text-rose-400 rounded">Pasif</span>}
                        </p>
                        {customer.company && (
                          <p className="text-xs text-zinc-500">{customer.company}</p>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-zinc-600 ml-auto" />
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="space-y-1">
                      {customer.email && (
                        <p className="text-xs text-zinc-400 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {customer.email}
                        </p>
                      )}
                      {customer.phone && (
                        <p className="text-xs text-zinc-400 flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {customer.phone}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono text-emerald-400">
                      {formatCurrency(customer.total_receivable || 0)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono text-rose-400">
                      {formatCurrency(customer.total_payable || 0)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openEditDialog(customer)}
                        className="hover:bg-zinc-800 text-zinc-400 hover:text-zinc-50"
                        data-testid={`edit-customer-${customer.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {isActive ? (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleArchive(customer.id)}
                          className="hover:bg-amber-400/10 text-zinc-400 hover:text-amber-400"
                          title="Pasife Al"
                          data-testid={`archive-customer-${customer.id}`}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleRestore(customer.id)}
                          className="hover:bg-emerald-400/10 text-zinc-400 hover:text-emerald-400"
                          title="Aktife Al"
                          data-testid={`restore-customer-${customer.id}`}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(customer.id)}
                        className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400"
                        data-testid={`delete-customer-${customer.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              );
              })}
              {filteredCustomers.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-zinc-500">
                    {search ? "Sonuç bulunamadı" : "Henüz müşteri eklenmemiş"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-outfit">
              {editingCustomer ? "Müşteri Düzenle" : "Yeni Müşteri"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-400">Müşteri Adı *</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">E-posta</Label>
                <Input 
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="customer-email-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Telefon</Label>
                <Input 
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="customer-phone-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Şirket</Label>
                <Input 
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="customer-company-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Adres</Label>
                <Textarea 
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  rows={2}
                  data-testid="customer-address-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Notlar</Label>
                <Textarea 
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  rows={2}
                  data-testid="customer-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-zinc-700 hover:bg-zinc-800"
              >
                İptal
              </Button>
              <Button 
                type="submit"
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                data-testid="save-customer-btn"
              >
                {editingCustomer ? "Güncelle" : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
